import { Component, HostListener, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { User } from '@mitel/cloudlink-sdk';
import { AdminService } from 'src/app/core/services/admin.service';
import { OrdersService } from 'src/app/core/services/orders.service';
import { mivoice5000Licenses } from 'src/app/core/constants/constants';
import * as _ from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { UserMitel5000SharedService } from 'src/app/core/services/user-mitel-5000-shared.service';

@Component({
  selector: 'app-mitel-5000-multi-edit-dialog',
  templateUrl: './mitel-5000-multi-edit-dialog.component.html',
  styleUrls: ['./mitel-5000-multi-edit-dialog.component.scss']
})
export class Mitel5000MultiEditDialogComponent implements OnInit {
  users: User[] = [];
  product: any;
  applyToAll: boolean;
  accountId: string;
  loading: boolean = true;
  mivoice5000Form = this.formBuilder.group({
    license: ['unchanged', [Validators.required]],
  });
  featureOptions = ['unchanged', 'enable', 'disable'];
  licenseOptions = ['unchanged'];
  mivoice5000LicenseInfo: any; // To show the summary breakdown
  submitted: boolean = false;
  collapsed = true; //default 
  longOperationInProgress = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<Mitel5000MultiEditDialogComponent>,
    private adminService: AdminService,
    private ordersService: OrdersService,
    private mivoice5000SharedSvc: UserMitel5000SharedService,
    private formBuilder: FormBuilder,
    private translateSvc: TranslateService
  ) { 
    this.product = data.product;
    this.users = data.users;
    this.applyToAll = data.applyToAll;
    this.accountId = data.accountId;
    dialogRef.addPanelClass("no-padding-dialog");
  }

   // to give the user the option to not close the page
  // when a lengthy operation is in progress
  @HostListener('window:beforeunload', ['$event'])
  unloadNotification(event: any) {
	  console.log("Browser page unload detected", event);
	  if ( this.longOperationInProgress === true )
	  {
		event.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
		// Chrome requires returnValue to be set
		event.returnValue = false;
	  }
  }

  async ngOnInit() {
    await this.getRequiredInformation();
    this.loading = false;
  }

  getRequiredInformation() {
    let promises = [];
  
    if (this.applyToAll) 
    {
      const summaryPromise = this.getRemainingLicenseDetails().then( remainingLicenses => {
          this.mivoice5000LicenseInfo = remainingLicenses;
          let licenseNames = Object.keys(this.mivoice5000LicenseInfo);
          let possibleTiers = [];
          for (let i = 0; i < licenseNames.length; i++) {
            let licenseInfo = this.mivoice5000LicenseInfo[licenseNames[i]];
            if (licenseInfo.used > 0 && !this.licenseOptions.includes('unassignAll')) {
              this.licenseOptions.push('unassignAll');
            }
            
            if ( licenseInfo.total >= this.users.length ) 
            {
              possibleTiers.push(licenseNames[i]);
            } 
          }
          this.licenseOptions.push(...possibleTiers);
      });
      promises.push(summaryPromise);
    } 
    else 
    {
      let summaryPromises = [];
      summaryPromises.push(this.getRemainingLicenseDetails());
      summaryPromises.push(this.getUserLicenseInformation());

      const summaryPromise = Promise.all(summaryPromises).then( values => 
      {
          if (values[0] && values[1] ) {
            let tempLicenseInformation = _.cloneDeep(values[0]);
            let licenseNames = Object.keys(tempLicenseInformation);

            for (let i = 0; i < licenseNames.length; i++ ) {
              if (values[1][licenseNames[i]]) {
                tempLicenseInformation[licenseNames[i]].used = values[1][licenseNames[i]].length;
                if (!this.licenseOptions.includes('unassignAll')) {
                  this.licenseOptions.push('unassignAll');
                }
              } else {
                tempLicenseInformation[licenseNames[i]].used = 0;
              }

              // Adding possible tier options based on available and used licenses
              const licenseTotal = tempLicenseInformation[licenseNames[i]].remaining + tempLicenseInformation[licenseNames[i]].used;
              if ( licenseTotal >= this.users.length ) 
              {
                this.licenseOptions.push(licenseNames[i]);
              } 
            }

            this.mivoice5000LicenseInfo = tempLicenseInformation; 
          }
      });
      promises.push(summaryPromise);
    }
    return Promise.all(promises);
  }

  
  getRemainingLicenseDetails() {
    return this.mivoice5000SharedSvc.getRemainingLicenseDetails();
  }

  getUserLicenseInformation() {
    return new Promise<any>(async (resolve, reject) => 
    {
      let userLicenseInformation = {};
      for (let i = 0; i < this.users.length; i++) {
        const userLicenses = await this.ordersService.getUsersLicensesFromCachedList(this.users[i].userId);
        const license = _.intersection(mivoice5000Licenses, userLicenses); 
        // There will be only one license after intersection, as there can be only one mitel 5000 license assigned to a user at a time.
        if (license.length == 1) {
          if (!userLicenseInformation[license[0]]) {
            userLicenseInformation[license[0]] = [this.users[i].userId];
          } else {
            userLicenseInformation[license[0]].push(this.users[i].userId);
          }
        }
      }
      resolve(userLicenseInformation);
    });
  }

  isFormValueChanged(): boolean {
    const result = this.getFormValues();
    if (Object.keys(result).length > 0) {
      return true;
    }
    return false;
  }

  getFormValues() {
    let result = {};
    const values = this.mivoice5000Form.value;
    for ( let key of Object.keys(values)) {
      
      if (values[key] !== 'unchanged') {
        result[key] = values[key];
      } 
    }
    
    return result;
  }

  onSave() {
    this.submitted = true;
    this.longOperationInProgress = true;
    const changes = this.getFormValues();
    console.log(changes);
    this.product.service.saveUserMitel5000BulkOperation(this.accountId, this.users, changes).then(result => {
      this.longOperationInProgress = false;
      this.dialogRef.close({reason: "resetUsers"});
    });
  }

  onCancel() {
    this.dialogRef.close();
  }

  onExpandItemClick() {
		this.collapsed = !this.collapsed;
	}

  getLicenseSummary() {
    
    let availableText = "", usedText = "";
    let totalAvailable = 0, totalUsed = 0;
    let licenseNames = Object.keys(this.mivoice5000LicenseInfo);
    for (let i = 0 ; i < licenseNames.length; i++) {
      availableText += ( " " + this.translateSvc.instant('users_multi_edit.mivoice5000.options.' + licenseNames[i]) + 
                          " (" + this.mivoice5000LicenseInfo[licenseNames[i]].remaining + ")" );

      usedText += ( " " + this.translateSvc.instant('users_multi_edit.mivoice5000.options.' + licenseNames[i]) + 
      " (" + this.mivoice5000LicenseInfo[licenseNames[i]].used + ")" );

      if (!(i == licenseNames.length - 1)) {
        availableText += ",";
        usedText += ",";
      }
      totalAvailable += this.mivoice5000LicenseInfo[licenseNames[i]].remaining;
      totalUsed += this.mivoice5000LicenseInfo[licenseNames[i]].used;
    }
    let summary = {
      available: totalAvailable,
      used: totalUsed,
      availableText,
      usedText
    };
    return summary;
  }

}
